import React from 'react';
import { Users, UserCheck, Coffee, UserX, UserMinus, AlertTriangle, Clock, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * KPI Summary Cards - Top row dashboard widgets
 * Shows: Total Employees, Checked In, On Break, Checked Out, Pending Approvals, Violations, Late
 */
const KPISummaryCards = ({ summary, loading }) => {
  const navigate = useNavigate();

  const handleCardClick = (cardTitle) => {
    switch (cardTitle) {
      case 'Total Employees':
        navigate('/home/members');
        break;
      case 'Checked In':
        navigate('/home/members', { state: { statusFilter: 'CHECKIN' } });
        break;
      case 'Checked Out':
        navigate('/home/members', { state: { statusFilter: 'CHECKOUT' } });
        break;
      case 'On Break':
        navigate('/home/members', { state: { statusFilter: 'BREAK' } });
        break;
      case 'Not Checked In':
        navigate('/home/members', { state: { statusFilter: 'NOT_CHECKED_IN' } });
        break;
      case 'Pending Approvals':
        navigate('/home/approvaltimeentries');
        break;
      case 'Geofence Violations':
        navigate('/home/locationTracking');
        break;
      case 'Late Arrivals':
        navigate('/home/reports');
        break;
      default:
        break;
    }
  };

  const cards = [
    {
      title: 'Total Employees',
      value: summary?.total_employees || 0,
      icon: Users,
      color: '#6366f1',
      bgColor: '#eef2ff',
    },
    {
      title: 'Checked In',
      value: summary?.checked_in || 0,
      icon: UserCheck,
      color: '#22c55e',
      bgColor: '#f0fdf4',
      subtitle: summary?.total_employees
        ? `${Math.round((summary.checked_in / summary.total_employees) * 100)}% present`
        : null,
    },
    {
      title: 'On Break',
      value: summary?.on_break || 0,
      icon: Coffee,
      color: '#f59e0b',
      bgColor: '#fffbeb',
    },
    {
      title: 'Checked Out',
      value: summary?.checked_out || 0,
      icon: UserX,
      color: '#6b7280',
      bgColor: '#f3f4f6',
    },
    {
      title: 'Not Checked In',
      value: summary?.not_checked_in || 0,
      icon: UserMinus,
      color: '#dc2626',
      bgColor: '#fef2f2',
      highlight: summary?.not_checked_in > 0,
    },
    {
      title: 'Pending Approvals',
      value: summary?.pending_approvals || 0,
      icon: ClipboardList,
      color: '#8b5cf6',
      bgColor: '#f5f3ff',
      highlight: summary?.pending_approvals > 0,
    },
    {
      title: 'Geofence Violations',
      value: summary?.geofence_violations_today || 0,
      icon: AlertTriangle,
      color: '#ef4444',
      bgColor: '#fef2f2',
      highlight: summary?.geofence_violations_today > 0,
    },
    {
      title: 'Late Arrivals',
      value: summary?.late_arrivals_today || 0,
      icon: Clock,
      color: '#f97316',
      bgColor: '#fff7ed',
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg p-3 shadow-sm animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-7 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
      {cards.map((card, index) => {
        const Icon = card.icon;
        return (
          <div
            key={index}
            onClick={() => handleCardClick(card.title)}
            className={`bg-white rounded-lg p-3 shadow-sm border transition-all hover:shadow-md cursor-pointer hover:scale-105 hover:bg-gray-50 ${
              card.highlight ? 'border-red-200 ring-1 ring-red-100' : 'border-gray-100'
            }`}
            style={{ cursor: 'pointer' }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-1000 uppercase tracking-wide">
                {card.title}
              </span>
              <div
                className="p-1.5 rounded-md"
                style={{ backgroundColor: card.bgColor }}
              >
                <Icon size={14} style={{ color: card.color }} />
              </div>
            </div>
            <div className="flex items-end justify-between">
              <span
                className="text-2xl font-bold"
                style={{ color: card.color }}
              >
                {card.value}
              </span>
              {card.subtitle && (
                <span className="text-xs text-gray-400">{card.subtitle}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KPISummaryCards;
